import { idbRequest, PENDING_RECORDINGS_STORE, withStore } from '@/db'

const TWENTY_FOUR_H_MS = 24 * 60 * 60 * 1000
const MAX_PENDING_PER_PROJECT = 3

const listeners = new Set<() => void>()

let sessionPruned = false

function notifyPendingRecordingsChanged(): void {
	for (const cb of listeners) {
		try {
			cb()
		} catch {}
	}
}

async function ensureSessionPrune(): Promise<void> {
	if (sessionPruned) {
		return
	}
	sessionPruned = true
	await pruneOlderThan(TWENTY_FOUR_H_MS)
}

export function subscribePendingRecordings(listener: () => void): () => void {
	listeners.add(listener)
	return () => listeners.delete(listener)
}

export interface PendingRecording {
	id: string
	projectId: string
	blob: Blob
	mimeType: string
	extension: string
	durationMs: number
	suggestedFilename: string
	recordedAt: number
}

export type PendingRecordingSaveInput = Omit<PendingRecording, 'id' | 'recordedAt'> & {
	recordedAt?: number
}

export async function pruneOlderThan(maxAgeMs: number): Promise<void> {
	const cutoff = Date.now() - maxAgeMs
	await withStore('readwrite', PENDING_RECORDINGS_STORE, async (store) => {
		const all = (await idbRequest(store.getAll())) as PendingRecording[]
		for (const row of all) {
			if (row.recordedAt < cutoff) {
				await idbRequest(store.delete(row.id))
			}
		}
	})
	notifyPendingRecordingsChanged()
}

export async function save(input: PendingRecordingSaveInput): Promise<string> {
	await ensureSessionPrune()
	const id = crypto.randomUUID()
	const recordedAt = input.recordedAt ?? Date.now()
	const row: PendingRecording = {
		...input,
		id,
		recordedAt,
	}

	const existing = await list(input.projectId)
	const byAge = [...existing].sort((a, b) => a.recordedAt - b.recordedAt)
	while (byAge.length >= MAX_PENDING_PER_PROJECT) {
		const evict = byAge.shift()
		if (evict) {
			await withStore('readwrite', PENDING_RECORDINGS_STORE, async (store) => {
				await idbRequest(store.delete(evict.id))
			})
		}
	}

	await withStore('readwrite', PENDING_RECORDINGS_STORE, async (store) => {
		await idbRequest(store.put(row))
	})
	notifyPendingRecordingsChanged()
	return id
}

export async function list(projectId: string): Promise<PendingRecording[]> {
	await ensureSessionPrune()
	const rows = await withStore('readonly', PENDING_RECORDINGS_STORE, async (store) => {
		const index = store.index('by_project')
		return idbRequest(index.getAll(projectId)) as Promise<PendingRecording[]>
	})
	return rows
		.filter((r) => !(r as { uploadedArtifactId?: string }).uploadedArtifactId)
		.sort((a, b) => b.recordedAt - a.recordedAt)
}

export async function getPendingRecording(id: string): Promise<PendingRecording | undefined> {
	return withStore('readonly', PENDING_RECORDINGS_STORE, async (store) => {
		return idbRequest(store.get(id)) as Promise<PendingRecording | undefined>
	})
}

export async function deleteEntry(id: string): Promise<void> {
	await withStore('readwrite', PENDING_RECORDINGS_STORE, async (store) => {
		await idbRequest(store.delete(id))
	})
	notifyPendingRecordingsChanged()
}
